/**
 * Настройки операционного агента — «Агенты» (приёмка накладных) и
 * «Ресторан» (начало суток, реквизиты). Только владелец.
 *
 * Каждая строка — ключ `ops.setting`, который агент уже читает; пределы и
 * формат проверяет сервер. Режим «проводит сам» — `posting.set_auto_mode`:
 * пока проба не пройдена, сервер откажет и назовёт её счёт.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { Notice } from '../../ui';
import { SETTINGS_KEYS, problemText, settingsApi } from './api';
import type { AgentSetting, AgentSettings, AutoPost } from './api';
import { SetSwitch } from './SetSwitch';

type Result = { ok: boolean; text: string } | null;

export function Agents({ group }: { group: 'receipts' | 'restaurant' }) {
  const query = useQuery<AgentSettings, ApiError>({
    queryKey: SETTINGS_KEYS.agent,
    queryFn: () => settingsApi.agent(),
  });
  if (query.isPending) return <Loading text="Загружаю настройки…" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const block = query.data.groups.find((item) => item.code === group);

  return (
    <div className="set-stack">
      {group === 'receipts' && <AutoPostCard state={query.data.auto_post} />}
      {block && (
        <section className="set-card" aria-label={block.title}>
          <h2 className="set-card__title">
            {group === 'receipts' ? 'Накладные' : 'Ресторан'}
          </h2>
          <p className="set-hint">
            {group === 'receipts'
              ? 'Как операционный агент сверяет фото накладных с черновиками StoreHouse и когда подаёт сигналы.'
              : 'Границы рабочего дня и реквизиты, по которым агент узнаёт печать ресторана.'}
          </p>
          <ul className="set-list">
            {block.items.map((item) => (
              <SettingRow key={item.key} item={item} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function AutoPostCard({ state }: { state: AutoPost }) {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const save = useMutation<{ message: string; auto_post: AutoPost }, ApiError, boolean>({
    mutationFn: (on) => settingsApi.setAutoPost(on),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      client.setQueryData<AgentSettings>(SETTINGS_KEYS.agent, (old) =>
        old ? { ...old, auto_post: data.auto_post } : old,
      );
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });
  const status = state.effective
    ? 'Агент сам проводит зелёные накладные.'
    : state.enabled
      ? 'Режим включён, но не действует: после ложной «зелёной» проба началась заново.'
      : 'Агент только советует: «можно проводить». Проводит человек в StoreHouse.';

  return (
    <section className="set-card" aria-label="Проведение накладных">
      <h2 className="set-card__title">Проведение накладных</h2>
      <p className="set-hint">
        Режим «проводит сам» включается только после пробы: агент должен подряд сверить с решением
        человека нужное число накладных без ложной «зелёной».
      </p>
      <SetSwitch
        label="Проводит сам"
        checked={state.enabled}
        disabled={save.isPending}
        onChange={(on) => {
          setResult(null);
          save.mutate(on);
        }}
      />
      <p className="set-value">{status}</p>
      <div className="set-trial">
        <div className="set-trial__bar" aria-hidden="true">
          <span
            style={{
              width: `${Math.min(100, Math.round((state.trial.reviewed / Math.max(1, state.trial.required)) * 100))}%`,
            }}
          />
        </div>
        <p className="set-hint">{state.trial.text}</p>
      </div>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

function asText(value: AgentSetting['value']): string {
  if (typeof value === 'string' && /^\d+\.\d{2}$/.test(value)) return value.replace('.', ',');
  return String(value);
}

export function SettingRow({ item }: { item: AgentSetting }) {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const [draft, setDraft] = useState(asText(item.value));
  useEffect(() => setDraft(asText(item.value)), [item.value]);
  const save = useMutation<{ message: string; setting: AgentSetting }, ApiError, unknown>({
    mutationFn: (value) => settingsApi.setAgent(item.key, value),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      client.setQueryData<AgentSettings>(SETTINGS_KEYS.agent, (old) =>
        old
          ? {
              ...old,
              groups: old.groups.map((group) => ({
                ...group,
                items: group.items.map((each) => (each.key === item.key ? data.setting : each)),
              })),
            }
          : old,
      );
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });
  const changed = draft.trim() !== asText(item.value);
  const isDefault = asText(item.value) === asText(item.default);

  return (
    <li className="set-row">
      <div className="set-row__main">
        <span className="set-row__title">{item.title}</span>
        <span className="set-row__hint">{item.hint}</span>
        {!isDefault && (
          <span className="set-row__default">
            по умолчанию: {item.kind === 'bool' ? (item.default ? 'да' : 'нет') : asText(item.default)}
            {item.unit && item.kind !== 'bool' ? ` ${item.unit}` : ''}
          </span>
        )}
      </div>
      <div className="set-row__control">
        {item.kind === 'bool' ? (
          <SetSwitch
            label={item.title}
            hideLabel
            checked={Boolean(item.value)}
            disabled={save.isPending}
            onChange={(on) => {
              setResult(null);
              save.mutate(on);
            }}
          />
        ) : (
          <form
            className="set-edit"
            onSubmit={(event) => {
              event.preventDefault();
              setResult(null);
              save.mutate(item.kind === 'int' ? draft.trim() : draft);
            }}
          >
            <input
              className={`input set-input set-input--${item.kind}`}
              type={item.kind === 'time' ? 'time' : 'text'}
              inputMode={item.kind === 'int' || item.kind === 'inn' ? 'numeric' : item.kind === 'money' ? 'decimal' : undefined}
              value={draft}
              aria-label={item.title}
              onChange={(event) => setDraft(event.target.value)}
            />
            {item.unit && <span className="set-unit">{item.unit}</span>}
            <button
              type="submit"
              className="button button--small"
              disabled={!changed || save.isPending || !draft.trim()}
            >
              Сохранить
            </button>
          </form>
        )}
      </div>
      {result && (
        <span className={`set-row__result${result.ok ? '' : ' set-row__result--error'}`}>
          {result.text}
        </span>
      )}
    </li>
  );
}
